import React from "react";
import styles from "./ScriptSkeleton.module.css";

export interface ScriptSkeletonProps {
  /**
   * How far the build has got. In "build" each stage reveals the next group
   * of pieces (0 name, 1 description, 2 section headings, 3 questions,
   * 4 grading notes); past the last group everything is shown and under
   * review. In "revise" the outline is already whole and the stage moves the
   * highlight through it.
   */
  stage: number;
  /** The real result has arrived: every piece settles. */
  isDone: boolean;
  /** "build" draws a new script from nothing; "revise" reworks an existing one. */
  variant: "build" | "revise";
}

type Kind = "title" | "line" | "heading" | "dot" | "note";
type Width = "w35" | "w40" | "w45" | "w50" | "w55" | "w60" | "w70" | "w75" | "w80" | "w90";

// A fixed, abstract shape: it stands in for a script without claiming how
// many sections or questions the real one will have.
const SECTIONS: { heading: Width; questions: { body: Width; note: Width }[] }[] = [
  { heading: "w45", questions: [{ body: "w80", note: "w50" }, { body: "w60", note: "w35" }] },
  {
    heading: "w35",
    questions: [
      { body: "w75", note: "w45" },
      { body: "w90", note: "w55" },
      { body: "w55", note: "w35" },
    ],
  },
  { heading: "w50", questions: [{ body: "w70", note: "w40" }, { body: "w80", note: "w50" }] },
];

// Which groups the highlight sits on at each revise stage: reading the
// script, finding the sections, rewriting questions, realigning the notes.
const REVISE_ACTIVE_GROUPS: number[][] = [[0, 1], [2], [3], [4]];

interface BarProps {
  kind: Kind;
  width?: Width;
  /** Position within its group, for the reveal stagger. */
  order?: number;
  /** Position down the outline, for the settle stagger. */
  row?: number;
  isVisible: boolean;
  isActive: boolean;
  isSettled: boolean;
}

const Bar = ({ kind, width, order = 0, row = 0, isVisible, isActive, isSettled }: BarProps) => {
  const classNames = [
    styles.bar,
    styles[kind],
    width && styles[width],
    order > 0 && styles[`d${Math.min(order, 7)}`],
    styles[`s${Math.min(row, 3)}`],
    isVisible && styles.visible,
    isActive && styles.active,
    isSettled && styles.settled,
  ];
  return <div className={classNames.filter(Boolean).join(" ")} />;
};

const ScriptSkeleton = ({ stage, isDone, variant }: ScriptSkeletonProps): React.ReactElement => {
  const isActive = (group: number) => {
    if (isDone) return false;
    if (variant === "revise") return (REVISE_ACTIVE_GROUPS[stage] ?? []).includes(group);
    return group === stage;
  };

  // Every piece of one group shares the same state.
  const stateOf = (group: number) => ({
    isVisible: isDone || variant === "revise" || group <= stage,
    isActive: isActive(group),
    isSettled: isDone,
  });

  // Questions are numbered across sections so the reveal runs top to bottom.
  let questionOrder = 0;

  return (
    <div className={styles.doc} aria-hidden="true">
      <Bar {...stateOf(0)} kind="title" width="w55" />
      <Bar {...stateOf(1)} kind="line" width="w90" />
      <Bar {...stateOf(1)} kind="line" width="w70" order={1} />

      {SECTIONS.map((section, sectionIndex) => (
        <div className={styles.section} key={sectionIndex}>
          <Bar
            {...stateOf(2)}
            kind="heading"
            width={section.heading}
            order={sectionIndex}
            row={sectionIndex + 1}
          />
          {section.questions.map((question, questionIndex) => {
            const order = questionOrder++;
            return (
              <div className={styles.question} key={questionIndex}>
                <Bar {...stateOf(3)} kind="dot" order={order} row={sectionIndex + 1} />
                <div className={styles.questionText}>
                  <Bar
                    {...stateOf(3)}
                    kind="line"
                    width={question.body}
                    order={order}
                    row={sectionIndex + 1}
                  />
                  <Bar
                    {...stateOf(4)}
                    kind="note"
                    width={question.note}
                    order={order}
                    row={sectionIndex + 1}
                  />
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default ScriptSkeleton;
